import { z } from "zod"

import { businessReportInputSchema } from "./businessReporting"
import type { BusinessReportInput } from "./businessReporting"
import type { ReportingOperations } from "./reportingOperations"
import { reportingOperationsSchema } from "./reportingOperations"
import type { ReportingSeoInventory } from "./reportingSeoInventory"
import { reportingInventoryPathSchema, reportingSeoInventorySchema } from "./reportingSeoInventory"

const MAX_REPORT_DAYS = 120
const DAY_MS = 86_400_000
export const REPORT_SCHEMA_VERSION = 1

export const reportingDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00Z`)
    return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value
  }, "Invalid calendar date")

const keySchema = z.string().regex(/^[a-z0-9][a-z0-9-]{0,79}$/)
const countSchema = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER)
const hostSchema = z
  .string()
  .max(253)
  .regex(/^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?$/)

export const reportingProductSchema = z
  .object({
    key: keySchema,
    name: z.string().trim().min(1).max(100),
    hosts: z.array(hostSchema).min(1).max(20),
    includedFrom: reportingDateSchema,
    active: z.boolean(),
  })
  .strict()
  .refine((value) => new Set(value.hosts).size === value.hosts.length, "Duplicate product hosts")

export const reportingSourceSchema = z
  .object({
    key: keySchema,
    productKey: keySchema,
    provider: z.enum(["gsc", "aix-seo", "posthog", "meta-ads", "google-ads"]),
    channel: z.enum(["organic-search", "product-analytics", "paid-social", "paid-search"]),
    externalId: z.string().trim().min(1).max(500),
    environment: z.literal("production"),
    timezone: z
      .string()
      .max(80)
      .refine((value) => {
        try {
          new Intl.DateTimeFormat("en", { timeZone: value })
          return true
        } catch {
          return false
        }
      }, "Invalid timezone"),
    currency: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .nullable(),
    attributionWindow: z.string().max(100).nullable(),
    toolPaths: z.array(reportingInventoryPathSchema).max(200).optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const channels = {
      gsc: "organic-search",
      "aix-seo": "organic-search",
      posthog: "product-analytics",
      "meta-ads": "paid-social",
      "google-ads": "paid-search",
    }
    if (value.channel !== channels[value.provider])
      ctx.addIssue({ code: "custom", message: "Provider/channel mismatch" })
    if (value.provider === "gsc" && value.timezone !== "America/Los_Angeles") {
      ctx.addIssue({ code: "custom", message: "GSC uses Pacific calendar dates" })
    }
    if (
      value.provider === "gsc" &&
      !/^sc-domain:[a-z0-9.-]+$/.test(value.externalId) &&
      !/^https:\/\/[^/?#]+\/$/.test(value.externalId)
    ) {
      ctx.addIssue({ code: "custom", message: "Expected a GSC domain or HTTPS URL-prefix property" })
    }
    if (
      (value.provider === "meta-ads" || value.provider === "google-ads") &&
      (!value.currency || !value.attributionWindow)
    ) {
      ctx.addIssue({ code: "custom", message: "Paid bindings require currency and attribution window" })
    }
    if (
      value.provider === "aix-seo" &&
      (!z.string().uuid().safeParse(value.externalId).success ||
        value.currency !== null ||
        value.attributionWindow !== null ||
        value.timezone !== "America/Los_Angeles")
    )
      ctx.addIssue({
        code: "custom",
        message: "AIX SEO requires a verified product UUID, Pacific timezone and no money attribution",
      })
    if (
      value.toolPaths &&
      (value.provider !== "aix-seo" ||
        new Set(value.toolPaths).size !== value.toolPaths.length ||
        value.toolPaths.includes("/"))
    )
      ctx.addIssue({ code: "custom", message: "Unique non-home tool paths apply only to AIX SEO bindings" })
  })

export const searchPerformanceGrainSchema = z.enum(["property", "page", "query"])
export const searchPerformanceDailyRowSchema = z
  .object({
    entity: z.string().max(2048),
    clicks: countSchema,
    impressions: countSchema,
    position: z.number().finite().nonnegative().nullable(),
  })
  .strict()

/** One native source day per atomic ingestion. No rolling-window values accepted. */
export const searchPerformanceDailyIngestSchema = z
  .object({
    attemptId: z.string().uuid(),
    sourceKey: keySchema,
    sourceProperty: z.string().min(1).max(500),
    date: reportingDateSchema,
    grain: searchPerformanceGrainSchema,
    dataState: z.literal("final"),
    paginationComplete: z.boolean(),
    status: z.enum(["available", "partial", "missing"]),
    errorCode: z.enum(["AUTH_UNAVAILABLE", "PROVIDER_ERROR", "ROW_CAP", "NO_OBSERVATION", "SOURCE_LAG"]).nullable(),
    queryVersion: z.literal("gsc-daily-v1"),
    rows: z.array(searchPerformanceDailyRowSchema).max(25_000),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.grain === "property" && (value.rows.length > 1 || value.rows.some((row) => row.entity !== ""))) {
      ctx.addIssue({ code: "custom", message: "Property grain has at most one row with empty entity" })
    }
    if (value.grain !== "property" && value.rows.some((row) => !row.entity))
      ctx.addIssue({ code: "custom", message: "Drilldown entity required" })
    if (new Set(value.rows.map((row) => row.entity)).size !== value.rows.length)
      ctx.addIssue({ code: "custom", message: "Duplicate entity" })
    if (value.status === "available" && (!value.paginationComplete || value.errorCode || !value.rows.length)) {
      ctx.addIssue({ code: "custom", message: "Available requires observed rows and completed pagination" })
    }
    if (value.status === "missing" && (value.rows.length || !value.errorCode))
      ctx.addIssue({ code: "custom", message: "Missing requires no rows and a reason" })
    if (value.status === "partial" && !value.errorCode)
      ctx.addIssue({ code: "custom", message: "Partial requires a reason" })
  })

export const reportingRangeSchema = z
  .object({
    start: reportingDateSchema,
    end: reportingDateSchema,
    productKeys: z.array(keySchema).min(1).max(20),
    operationalWeekEnding: reportingDateSchema.optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const days = (Date.parse(value.end) - Date.parse(value.start)) / DAY_MS + 1
    if (days < 1 || days > MAX_REPORT_DAYS)
      ctx.addIssue({ code: "custom", message: "Range must contain 1–120 inclusive days" })
    if (new Set(value.productKeys).size !== value.productKeys.length)
      ctx.addIssue({ code: "custom", message: "Duplicate product keys" })
    if (
      value.operationalWeekEnding &&
      (new Date(value.operationalWeekEnding).getUTCDay() !== 0 || value.end > value.operationalWeekEnding)
    )
      ctx.addIssue({
        code: "custom",
        message: "Operational week must end Sunday and source data must not extend past it",
      })
  })

export type ReportingProduct = z.infer<typeof reportingProductSchema>
export type ReportingSource = z.infer<typeof reportingSourceSchema>
export type SearchPerformanceDailyIngest = z.infer<typeof searchPerformanceDailyIngestSchema>
export type ReportingRange = z.infer<typeof reportingRangeSchema>

export interface ReportingSearchPerformanceDay extends SearchPerformanceDailyIngest {
  productKey: string
  collectionId: string
  collectedAt: string
  latestAttemptStatus: SearchPerformanceDailyIngest["status"]
}

export const searchIngestionStatusRequestSchema = z
  .object({
    sourceKey: keySchema,
    start: reportingDateSchema,
    end: reportingDateSchema,
    expectedAttempts: z
      .array(
        z
          .object({
            date: reportingDateSchema,
            grain: searchPerformanceGrainSchema,
            attemptId: z.string().uuid(),
            digest: z.string().regex(/^[a-f0-9]{64}$/),
            status: z.enum(["available", "partial", "missing"]),
          })
          .strict(),
      )
      .max(168)
      .optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const days = (Date.parse(value.end) - Date.parse(value.start)) / DAY_MS + 1
    if (days < 1 || days > MAX_REPORT_DAYS)
      ctx.addIssue({ code: "custom", message: "Range must contain 1–120 inclusive days" })
    if (value.expectedAttempts?.some((attempt) => attempt.date < value.start || attempt.date > value.end))
      ctx.addIssue({ code: "custom", message: "Expected attempt must be inside the status range" })
  })

const searchIngestionStatusObservationSchema = z
  .object({
    date: reportingDateSchema,
    grain: searchPerformanceGrainSchema,
    selectedAttemptId: z.string().uuid().nullable(),
    selectedStatus: z.enum(["available", "partial", "missing"]).nullable(),
    latestAttemptId: z.string().uuid().nullable(),
    latestStatus: z.enum(["available", "partial", "missing"]).nullable(),
    latestCollectedAt: z.string().datetime({ offset: true }).nullable(),
  })
  .strict()

const searchIngestionAttemptCheckSchema = z
  .object({
    date: reportingDateSchema,
    grain: searchPerformanceGrainSchema,
    attemptId: z.string().uuid(),
    found: z.boolean(),
    matches: z.boolean(),
    collectionId: z.string().uuid().nullable(),
    digest: z.string().nullable(),
    status: z.enum(["available", "partial", "missing"]).nullable(),
  })
  .strict()

export const searchIngestionStatusSchema = z
  .object({
    companyId: z.string().uuid(),
    sourceKey: keySchema,
    productKey: keySchema,
    sourceProperty: z.string().min(1).max(500),
    start: reportingDateSchema,
    end: reportingDateSchema,
    requestedDates: z.array(reportingDateSchema).min(1).max(MAX_REPORT_DAYS),
    observations: z.array(searchIngestionStatusObservationSchema).max(MAX_REPORT_DAYS * 3),
    attemptChecks: z.array(searchIngestionAttemptCheckSchema).max(168),
    missingCount: countSchema,
    partialCount: countSchema,
  })
  .strict()
  .superRefine((value, ctx) => {
    const expectedDates = reportingDates(value.start, value.end)
    if (canonicalReportingJson(value.requestedDates) !== canonicalReportingJson(expectedDates))
      ctx.addIssue({ code: "custom", message: "Requested dates do not match status range" })
    const expectedKeys = new Set(
      expectedDates.flatMap((date) => ["property", "page", "query"].map((grain) => `${date}:${grain}`)),
    )
    const observedKeys = value.observations.map((observation) => `${observation.date}:${observation.grain}`)
    if (
      observedKeys.length !== expectedKeys.size ||
      new Set(observedKeys).size !== observedKeys.length ||
      observedKeys.some((key) => !expectedKeys.has(key))
    )
      ctx.addIssue({ code: "custom", message: "Status observations must cover every requested date and grain" })
    const missingCount = value.observations.filter(
      (observation) => observation.selectedStatus === null || observation.selectedStatus === "missing",
    ).length
    const partialCount = value.observations.filter((observation) => observation.selectedStatus === "partial").length
    if (value.missingCount !== missingCount || value.partialCount !== partialCount)
      ctx.addIssue({ code: "custom", message: "Status counts do not match observations" })
  })

export type SearchIngestionStatusRequest = z.infer<typeof searchIngestionStatusRequestSchema>
export type SearchIngestionStatus = z.infer<typeof searchIngestionStatusSchema>

export interface ReportingSeoData {
  schemaVersion: typeof REPORT_SCHEMA_VERSION
  range: ReportingRange
  products: ReportingProduct[]
  sources: ReportingSource[]
  days: ReportingSearchPerformanceDay[]
  operations?: ReportingOperations
  seoInventory?: ReportingSeoInventory
}

export const reportingSeoDataSchema = z
  .object({
    schemaVersion: z.literal(REPORT_SCHEMA_VERSION),
    range: reportingRangeSchema,
    products: z.array(reportingProductSchema).min(1).max(20),
    sources: z.array(reportingSourceSchema).max(80),
    days: z
      .array(
        searchPerformanceDailyIngestSchema
          .innerType()
          .extend({
            productKey: keySchema,
            collectionId: z.string().uuid(),
            collectedAt: z.string().datetime({ offset: true }),
            latestAttemptStatus: z.enum(["available", "partial", "missing"]),
          })
          .strict()
          .superRefine((value, ctx) => {
            const {
              productKey: _productKey,
              collectionId: _collectionId,
              collectedAt: _collectedAt,
              latestAttemptStatus: _latestAttemptStatus,
              ...receipt
            } = value
            const parsed = searchPerformanceDailyIngestSchema.safeParse(receipt)
            if (!parsed.success) for (const issue of parsed.error.issues) ctx.addIssue(issue)
          }),
      )
      .max(7200),
    operations: reportingOperationsSchema.optional(),
    seoInventory: reportingSeoInventorySchema.optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const products = new Set(value.products.map((product) => product.key))
    if (
      value.operations &&
      (value.operations.products.length !== products.size ||
        value.operations.products.some((product) => !products.has(product.productKey)))
    )
      ctx.addIssue({ code: "custom", message: "Operational roster does not match report" })
    if (
      value.operations &&
      value.range.operationalWeekEnding &&
      canonicalReportingJson(value.operations.current) !==
        canonicalReportingJson(reportingWeekEnding(value.range.operationalWeekEnding))
    )
      ctx.addIssue({ code: "custom", message: "Operational window differs from requested week" })
    const sources = new Map(value.sources.map((source) => [source.key, source]))
    if (value.seoInventory) {
      if (
        value.seoInventory.products.length !== products.size ||
        value.seoInventory.products.some((product) => !products.has(product.productKey))
      )
        ctx.addIssue({ code: "custom", message: "SEO inventory roster does not match report" })
      for (const inventory of value.seoInventory.products) {
        if (inventory.status !== "available") continue
        const source = sources.get(inventory.sourceKey ?? "")
        const product = value.products.find((candidate) => candidate.key === inventory.productKey)
        if (
          source?.provider !== "aix-seo" ||
          source.productKey !== inventory.productKey ||
          source.externalId.toLowerCase() !== inventory.seoProductId?.toLowerCase() ||
          !product?.hosts.includes(inventory.domain ?? "")
        )
          ctx.addIssue({ code: "custom", message: "SEO inventory binding does not match report ownership" })
      }
    }
    const hosts = value.products.flatMap((product) => product.hosts)
    if (products.size !== value.products.length || new Set(hosts).size !== hosts.length)
      ctx.addIssue({ code: "custom", message: "Duplicate product identity or hostname" })
    if (sources.size !== value.sources.length || value.sources.some((source) => !products.has(source.productKey)))
      ctx.addIssue({ code: "custom", message: "Invalid source identity" })
    if (value.range.productKeys.length !== products.size || value.range.productKeys.some((key) => !products.has(key)))
      ctx.addIssue({ code: "custom", message: "Report roster does not match requested products" })
    const uniqueDays = new Set<string>()
    for (const day of value.days) {
      const source = sources.get(day.sourceKey)
      const key = `${day.sourceKey}:${day.date}:${day.grain}`
      if (
        uniqueDays.has(key) ||
        day.date < value.range.start ||
        day.date > value.range.end ||
        source?.productKey !== day.productKey ||
        source?.externalId !== day.sourceProperty ||
        source?.provider !== "gsc"
      )
        ctx.addIssue({ code: "custom", message: "Invalid or duplicate daily collection identity" })
      uniqueDays.add(key)
    }
  })

/** Historical version 1 editions retain their original interpretation; version 2 is parsed strictly. */
export const reportingSnapshotInputSchema = z.union([reportingSeoDataSchema, businessReportInputSchema])
export type ReportingSnapshotInput = ReportingSeoData | BusinessReportInput

export function reportingDates(start: string, end: string): string[] {
  reportingRangeSchema.parse({ start, end, productKeys: ["validation"] })
  const dates: string[] = []
  for (let date = Date.parse(start); date <= Date.parse(end); date += DAY_MS)
    dates.push(new Date(date).toISOString().slice(0, 10))
  return dates
}

export function canonicalReportingJson(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value)
  if (Array.isArray(value)) return `[${value.map(canonicalReportingJson).join(",")}]`
  const record = value as Record<string, unknown>
  return `{${Object.keys(record)
    .sort()
    .filter((key) => record[key] !== undefined)
    .map((key) => `${JSON.stringify(key)}:${canonicalReportingJson(record[key])}`)
    .join(",")}}`
}

/** Pacific midnight conversion uses IANA offsets, not a fixed 24-hour week. */
export function pacificMidnight(date: string): string {
  const target = Date.parse(`${date}T00:00:00Z`)
  let instant = target
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  })
  for (let attempt = 0; attempt < 3; attempt++) {
    const parts = Object.fromEntries(formatter.formatToParts(new Date(instant)).map((part) => [part.type, part.value]))
    const localAsUtc = Date.parse(
      `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}Z`,
    )
    instant += target - localAsUtc
  }
  return new Date(instant).toISOString()
}

export function reportingWeekEnding(end: string) {
  reportingDateSchema.parse(end)
  if (new Date(end).getUTCDay() !== 0) throw new Error("Operational week must end Sunday")
  const start = new Date(Date.parse(end) - 6 * DAY_MS).toISOString().slice(0, 10)
  const endExclusive = new Date(Date.parse(end) + DAY_MS).toISOString().slice(0, 10)
  return { startInclusive: pacificMidnight(start), endExclusive: pacificMidnight(endExclusive) }
}

export function previousCompleteReportingWeek(now: Date) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Los_Angeles",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
      .formatToParts(now)
      .map((part) => [part.type, part.value]),
  )
  const today = Date.parse(`${parts.year}-${parts.month}-${parts.day}T00:00:00Z`)
  const monday = today - ((new Date(today).getUTCDay() + 6) % 7) * DAY_MS
  const start = new Date(monday - 7 * DAY_MS).toISOString().slice(0, 10)
  const endExclusiveDate = new Date(monday).toISOString().slice(0, 10)
  return {
    start,
    end: new Date(monday - DAY_MS).toISOString().slice(0, 10),
    startInclusive: pacificMidnight(start),
    endExclusive: pacificMidnight(endExclusiveDate),
    timezone: "America/Los_Angeles" as const,
  }
}
